// Form validation utilities
import type { ProductFilesFormValues } from "@/screens/Products/components/Product/Information/schemas/ProductFilesSchema"
import { ProductFileType } from "@/screens/Products/components/Product/Information/types/productFile"

export type FileFormData = {
  fileType?: ProductFileType
  file?: File
  fileDescription?: string
} | null

export const isDescriptionRequired = (fileType?: ProductFileType): boolean =>
  fileType === ProductFileType.Labwork || fileType === ProductFileType.Miscellaneous

export const isFormCompletelyFilled = (formData: FileFormData): boolean => {
  if (!formData) return false
  const { fileType, file, fileDescription } = formData
  return !!(file && fileType && (!isDescriptionRequired(fileType) || fileDescription?.trim()))
}

export const isFormCompletelyEmpty = (formData: FileFormData): boolean => {
  if (!formData) return true
  const { fileType, file, fileDescription } = formData
  return !fileType && !file && !fileDescription
}

export const isFormPartiallyFilled = (formData: FileFormData): boolean => {
  if (!formData) return false
  return !isFormCompletelyEmpty(formData) && !isFormCompletelyFilled(formData)
}

// Button logic utilities
export const canShowAddButton = (values: ProductFilesFormValues, fileNumber: number): boolean => {
  if (!values) return false

  if (fileNumber === 1) {
    return isFormCompletelyFilled(values[0])
  }

  const allForms = Object.values(values)
  return allForms.every((form) => isFormCompletelyFilled(form))
}

export const canSubmit = (values: ProductFilesFormValues, isDirty: boolean, isSubmitting: boolean): boolean => {
  if (isSubmitting || !values || !isDirty) return false

  const allForms = Object.values(values)
  const filledForms = allForms.filter((form) => isFormCompletelyFilled(form))
  const partiallyFilledForms = allForms.filter((form) => isFormPartiallyFilled(form))

  // Must have at least one filled form and no partially filled forms
  if (filledForms.length === 0 || partiallyFilledForms.length > 0) return false

  // All non-empty forms must be valid
  return allForms.every((form) => isFormCompletelyEmpty(form) || isFormCompletelyFilled(form))
}
